import {
  ILocalePair,
  IRelatedLink,
  IMethodsIdMethod,
  TMethodsIdApi,
  TRelatedScope,
} from "@/@types/servers/methods-id";

export interface ITreeMethod {
  id: string;
  type: string;
  name: ILocalePair;
  entry_point: string;
}

export interface ITreeModule {
  /** "Server_Key/Route_Key" — адрес модуля внутри Methods_ID */
  ref: string;
  key: string;
  name: ILocalePair;
  related: IRelatedLink[];
  methods: ITreeMethod[];
}

export interface ITreeServer {
  key: string;
  name: ILocalePair;
  port: number;
  /** Видимость для администратора (show в Methods_ID) */
  show: boolean;
  modules: ITreeModule[];
}

/** read → только GET, write → всё кроме GET, all → всё */
export function scopePredicate(
  scope: TRelatedScope
): (type: string) => boolean {
  if (scope === "read") return (t) => t === "GET";
  if (scope === "write") return (t) => t !== "GET";
  return () => true;
}

function toPair(
  field: ILocalePair | string | Array<{ lang: string; name: string }>
): ILocalePair {
  if (typeof field === "string") return { ru: field, en: field };
  if (Array.isArray(field)) {
    const ru = field.find((l) => l.lang == "ru");
    const en = field.find((l) => l.lang == "en");
    return {
      ru: ru ? ru.name : en ? en.name : "",
      en: en ? en.name : ru ? ru.name : "",
    };
  }
  return field;
}

function toMethod(method: IMethodsIdMethod): ITreeMethod {
  return {
    id: method.id,
    type: method.type,
    name: toPair(method.name as ILocalePair),
    entry_point: method.entry_point,
  };
}

/** старый формат /api/methods (массив IMain) */
type TNameField = ILocalePair | string | Array<{ lang: string; name: string }>;

interface ILegacyServer {
  name: string;
  port?: number;
  show?: boolean;
  locale?: TNameField;
  routes?: Array<{
    name: string;
    locale?: TNameField;
    related?: IRelatedLink[];
    methods?: Array<IMethodsIdMethod>;
  }>;
}

/**
 * Приводит ответ /api/methods к плоскому дереву сервер → модуль → метод.
 * Поддерживает старый (массив IMain с locale[]) и новый (объект Methods_ID с related) форматы.
 */
export function normalizeMethodsTree(api: unknown): ITreeServer[] {
  const tree: ITreeServer[] = [];

  if (Array.isArray(api)) {
    for (const server of api as ILegacyServer[]) {
      const modules: ITreeModule[] = [];
      for (const route of server.routes || []) {
        modules.push({
          ref: `${server.name}/${route.name}`,
          key: route.name,
          name: toPair(route.locale || route.name),
          related: (route.related as IRelatedLink[]) || [],
          methods: (route.methods || []).map(toMethod),
        });
      }
      tree.push({
        key: String(server.name),
        name: toPair(server.locale || server.name),
        port: Number(server.port || 0),
        show: server.show !== false,
        modules,
      });
    }
    return tree;
  }

  const servers = api as TMethodsIdApi;
  if (!servers || typeof servers !== "object") return tree;

  for (const serverKey of Object.keys(servers)) {
    const server = servers[serverKey];
    const modules: ITreeModule[] = [];
    for (const routeKey of Object.keys(server.routes || {})) {
      const route = server.routes[routeKey];
      modules.push({
        ref: `${serverKey}/${routeKey}`,
        key: routeKey,
        name: toPair(route.name),
        related: route.related || [],
        methods: (route.methods || []).map(toMethod),
      });
    }
    tree.push({
      key: serverKey,
      name: toPair(server.name),
      port: Number(server.port || 0),
      show: server.show !== false,
      modules,
    });
  }
  return tree;
}

/**
 * Оставляет в дереве только методы из permitions (права создателя).
 * null → без фильтра (root видит всё)
 */
export function filterTreeByPermissions(
  tree: ITreeServer[],
  permitions: string[] | null
): ITreeServer[] {
  if (!permitions) return tree;
  const set = new Set(permitions);
  return tree
    .map((server) => ({
      ...server,
      modules: server.modules
        .map((mod) => ({
          ...mod,
          methods: mod.methods.filter((m) => set.has(m.id) || !m.id),
        }))
        .filter((mod) => mod.methods.length),
    }))
    .filter((server) => server.modules.length);
}

/** Индекс модулей по ref для резолва зависимостей */
export function buildModuleIndex(
  tree: ITreeServer[]
): Map<string, ITreeModule> {
  const index = new Map<string, ITreeModule>();
  for (const server of tree) {
    for (const mod of server.modules) index.set(mod.ref, mod);
  }
  return index;
}

/** id методов модуля, покрытых связью (scope) */
export function relatedMethodIds(
  mod: ITreeModule,
  link: IRelatedLink,
  extraFilter?: (id: string) => boolean
): string[] {
  const pred = scopePredicate(link.scope);
  return mod.methods
    .filter((m) => pred(m.type) && (!extraFilter || extraFilter(m.id)))
    .map((m) => m.id);
}
